/**
 * Admin panel — gated to user.role === 'ADMIN' (single admin: configured email).
 * Reached at /admin. Three tabs: Stats / Users / Invite codes.
 */
import { useEffect, useState } from 'react';

interface Stats {
  totalUsers: number;
  guests: number;
  subscribers: number;
  admins: number;
  generations: number;
  inviteCodes: number;
}

interface AdminUser {
  id: string;
  email: string;
  role: 'GUEST' | 'SUBSCRIBER' | 'ADMIN';
  subscriptionStatus: string | null;
  subscriptionEndsAt: string | null;
  emailVerifiedAt: string | null;
  inviteBonusType: string | null;
  lifetimeFree: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  _count: { generations: number };
}

interface InviteCode {
  id: string;
  code: string;
  note: string | null;
  maxUses: number;
  uses: number;
  bonusType: string;
  expiresAt: string | null;
  createdAt: string;
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

async function api(method: 'GET' | 'POST', path: string, body?: unknown): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (method !== 'GET') {
    const csrf = readCookie('aiprompt_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch('/api/admin' + path, {
    method,
    credentials: 'same-origin',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(json?.error?.message || `HTTP ${res.status}`);
  return json;
}

type Tab = 'stats' | 'users' | 'codes';

export function AdminPanel({ onLeave }: { onLeave: () => void }) {
  const [tab, setTab] = useState<Tab>('stats');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="admin">
      <div className="admin__bar">
        <div className="admin__title">AiPrompt.si admin</div>
        <button type="button" className="button button--ghost button--small" onClick={onLeave}>
          ← Back to app
        </button>
      </div>
      <div className="admin__tabs">
        {(['stats', 'users', 'codes'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`admin__tab${tab === t ? ' admin__tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'stats' ? 'Stats' : t === 'users' ? 'Users' : 'Invite codes'}
          </button>
        ))}
      </div>
      {error && <div className="modal__error">{error}</div>}
      <div className="admin__panel">
        {tab === 'stats' && <StatsTab onError={setError} />}
        {tab === 'users' && <UsersTab onError={setError} />}
        {tab === 'codes' && <CodesTab onError={setError} />}
      </div>
    </div>
  );
}

function StatsTab({ onError }: { onError: (m: string) => void }) {
  const [stats, setStats] = useState<Stats | null>(null);
  useEffect(() => {
    api('GET', '/stats').then((j) => setStats(j)).catch((e) => onError(e.message));
  }, [onError]);
  if (!stats) return <p>Loading…</p>;
  return (
    <div className="admin__stats">
      <Stat label="Total users" value={stats.totalUsers} />
      <Stat label="Guests" value={stats.guests} />
      <Stat label="Subscribers" value={stats.subscribers} accent />
      <Stat label="Admins" value={stats.admins} />
      <Stat label="Generations" value={stats.generations} />
      <Stat label="Invite codes" value={stats.inviteCodes} />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`admin__stat${accent ? ' admin__stat--accent' : ''}`}>
      <div className="admin__stat-value">{value}</div>
      <div className="admin__stat-label">{label}</div>
    </div>
  );
}

function UsersTab({ onError }: { onError: (m: string) => void }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function reload() {
    api('GET', '/users?limit=200').then((j) => setUsers(j.users)).catch((e) => onError(e.message));
  }
  useEffect(reload, [onError]);

  async function changeRole(id: string, role: string) {
    setBusy(id);
    try {
      await api('POST', `/users/${id}/role`, { role });
      reload();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(null);
    }
  }

  async function toggleLifetime(u: AdminUser) {
    const next = !u.lifetimeFree;
    // Revoking is the destructive direction — two-step confirm so a
    // mis-click doesn't strip someone's perks. Granting is cheap.
    if (!next) {
      const ok = window.confirm(
        `Revoke Lifetime free for ${u.email}?\n\nThey'll drop back to Guest tier (or whatever Stripe / role indicates) on their next request. They will NOT receive a notification.`,
      );
      if (!ok) return;
    }
    setBusy(u.id);
    try {
      await api('POST', `/users/${u.id}/lifetime`, { enabled: next });
      reload();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(null);
    }
  }

  if (!users) return <p>Loading…</p>;
  if (users.length === 0) return <p className="muted">No users yet.</p>;
  return (
    <table className="admin__table">
      <thead>
        <tr>
          <th>Email</th>
          <th>Role</th>
          <th>Lifetime</th>
          <th>Verified</th>
          <th>Sub ends</th>
          <th>Gens</th>
          <th>Last login</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => (
          <tr key={u.id}>
            <td className="admin__email">{u.email}</td>
            <td data-label="Role">
              <span className={`auth-bar__tier auth-bar__tier--${u.role.toLowerCase()}`}>
                {u.role === 'SUBSCRIBER' ? 'Subscriber' : u.role === 'ADMIN' ? 'Admin' : 'Free'}
              </span>
            </td>
            <td data-label="Lifetime">
              <button
                type="button"
                className={`lifetime-toggle${u.lifetimeFree ? ' lifetime-toggle--on' : ''}`}
                onClick={() => void toggleLifetime(u)}
                disabled={busy === u.id}
                title={
                  u.lifetimeFree
                    ? 'Lifetime free is ON — click to revoke'
                    : 'Lifetime free is OFF — click to grant + send welcome email'
                }
              >
                {u.lifetimeFree ? 'On' : 'Off'}
              </button>
            </td>
            <td data-label="Verified">{u.emailVerifiedAt ? '✓' : '—'}</td>
            <td data-label="Sub ends">{u.subscriptionEndsAt ? new Date(u.subscriptionEndsAt).toLocaleDateString() : '—'}</td>
            <td data-label="Gens">{u._count.generations}</td>
            <td data-label="Last login">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</td>
            <td data-label="Created">{new Date(u.createdAt).toLocaleDateString()}</td>
            <td data-label="Actions">
              <div className="admin__actions">
                <select
                  value={u.role}
                  disabled={busy === u.id}
                  onChange={(e) => void changeRole(u.id, e.target.value)}
                  aria-label="Role"
                >
                  <option value="GUEST">Guest</option>
                  <option value="SUBSCRIBER">Subscriber</option>
                  <option value="ADMIN">Admin</option>
                </select>
                {u.role !== 'ADMIN' && (
                  <GiftCell user={u} busy={busy === u.id} onBefore={() => setBusy(u.id)} onAfter={() => { setBusy(null); reload(); }} onError={onError} />
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Inline gift control next to the role select. Extends a user's
 * subscription by N months without Stripe — useful for partners, early
 * adopters, or extending a trial. Confirms before sending to avoid
 * fat-finger gifts.
 */
function GiftCell({
  user,
  busy,
  onBefore,
  onAfter,
  onError,
}: {
  user: AdminUser;
  busy: boolean;
  onBefore: () => void;
  onAfter: () => void;
  onError: (m: string) => void;
}) {
  const [months, setMonths] = useState(1);
  const abs = Math.abs(months);
  const isAdd = months > 0;
  const isRemove = months < 0;
  const valid = months !== 0 && abs <= 120;

  async function apply() {
    if (!valid) return;
    const verb = isAdd ? 'Gift' : 'Remove';
    const prep = isAdd ? 'to' : 'from';
    const tail = user.subscriptionEndsAt
      ? `Current end: ${new Date(user.subscriptionEndsAt).toLocaleDateString()} — ${isAdd ? 'extends from' : 'subtracted from'} this date.`
      : isAdd
        ? 'Starts from today.'
        : 'No active subscription — removing will leave the user as Guest.';
    const ok = window.confirm(
      `${verb} ${abs} month${abs === 1 ? '' : 's'} ${prep} ${user.email}?\n\n${tail}`,
    );
    if (!ok) return;
    onBefore();
    try {
      await api('POST', `/users/${user.id}/gift-subscription`, { months });
      onAfter();
    } catch (e: any) {
      onError(e.message);
      onAfter();
    }
  }

  const buttonLabel = !valid
    ? 'Set ±N'
    : isAdd
      ? `Gift ${abs}mo`
      : `Remove ${abs}mo`;
  const buttonTitle = !valid
    ? 'Enter a non-zero number between -120 and 120'
    : isAdd
      ? `Gift ${abs} month${abs === 1 ? '' : 's'} of Subscriber access`
      : `Remove ${abs} month${abs === 1 ? '' : 's'} of Subscriber access (negative = revoke time)`;

  return (
    <div className="admin__gift">
      <input
        type="number"
        min={-120}
        max={120}
        value={months}
        disabled={busy}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '' || raw === '-') {
            setMonths(0);
            return;
          }
          const n = Math.floor(Number(raw));
          if (!Number.isFinite(n)) return;
          setMonths(Math.max(-120, Math.min(120, n)));
        }}
        aria-label="Months to gift (negative to remove)"
        className={`admin__gift-input${isRemove ? ' admin__gift-input--remove' : ''}`}
      />
      <button
        type="button"
        className={`button button--small${isRemove ? ' button--ghost admin__gift-btn--remove' : ' button--ghost'}`}
        onClick={() => void apply()}
        disabled={busy || !valid}
        title={buttonTitle}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

function CodesTab({ onError }: { onError: (m: string) => void }) {
  const [codes, setCodes] = useState<InviteCode[] | null>(null);
  const [bonusType, setBonusType] = useState('trial_7d');
  const [maxUses, setMaxUses] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  function reload() {
    api('GET', '/invite-codes').then((j) => setCodes(j.codes)).catch((e) => onError(e.message));
  }
  useEffect(reload, [onError]);

  async function create() {
    setBusy(true);
    try {
      await api('POST', '/invite-codes', { bonusType, maxUses, note: note || undefined });
      setNote('');
      reload();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function disable(id: string) {
    setBusy(true);
    try {
      await api('POST', `/invite-codes/${id}/disable`);
      reload();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="admin__create">
        <div className="admin__create-row">
          <label>
            <span>Bonus</span>
            <select value={bonusType} onChange={(e) => setBonusType(e.target.value)}>
              <option value="trial_7d">7-day Subscriber trial</option>
              <option value="trial_30d">30-day Subscriber trial</option>
              <option value="none">None (referral only)</option>
            </select>
          </label>
          <label>
            <span>Max uses</span>
            <input
              type="number"
              min={1}
              max={1000}
              value={maxUses}
              onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
            />
          </label>
          <label className="admin__create-note">
            <span>Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="who's it for?" />
          </label>
          <button
            type="button"
            className="button button--primary"
            onClick={() => void create()}
            disabled={busy}
          >
            {busy ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </div>

      {!codes && <p>Loading…</p>}
      {codes && codes.length === 0 && <p className="muted">No invite codes yet.</p>}
      {codes && codes.length > 0 && (
        <table className="admin__table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Bonus</th>
              <th>Uses</th>
              <th>Expires</th>
              <th>Note</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => {
              const isExpired = c.expiresAt && new Date(c.expiresAt) < new Date();
              const isExhausted = c.uses >= c.maxUses;
              return (
                <tr key={c.id}>
                  <td className="admin__email" data-label="Code"><code>{c.code}</code></td>
                  <td data-label="Bonus">{c.bonusType}</td>
                  <td data-label="Uses">{c.uses} / {c.maxUses}</td>
                  <td data-label="Expires">{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—'}</td>
                  <td data-label="Note">{c.note || '—'}</td>
                  <td data-label="Created">{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td data-label="Action">
                    {isExpired ? (
                      <span className="muted">expired</span>
                    ) : isExhausted ? (
                      <span className="muted">exhausted</span>
                    ) : (
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={busy}
                        onClick={() => void disable(c.id)}
                      >
                        Disable
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
